import { lazy, Suspense, useState } from 'react';
import { CoreBox, CoreTypography } from '@youscience/core';
import { searchFilters } from '@common-types';

import useHomePageStore from '@store/HomePageStore';
import useUserInfoStore from '@store/UserInfoStore';
import useMajorSearchStore from '@store/MajorSearchStore';
import useAuthStore from '@store/AuthStore';

import { getSchoolDetailsPath, ROUTES } from '@constants/ROUTES';
import { SLIDER_ITEMS_LIMIT } from '@constants';

import { Breadcrumbs } from '@components/Breadcrumbs';
import { CardSlider, CardType } from '@components/CardSlider';

import defaultSchoolImg from '@images/default-school-details.png';
import UrlIcon from '@images/url.svg?react';

import AreasSection from './components/AreasSection';
import RelatedScholarships from './components/RelatedScholarships';

const PartnerSchoolDialog = lazy(() => import('@components/PartnerSchoolDialog'));

import {
  sxStyles,
  StyledHeroSection,
  StyledHeroText,
  StyledHeroImg,
  StyledSchoolDetailsBtn,
  StyledWebsiteLink,
} from './LandingPage.styles';

export const LandingPage = () => {
  const {
    landingPageSchoolData: {
      id,
      areasOfStudy = [],
      favoriteMajors = [],
      fitMajors = [],
      favoritedCareerMajors = [],
      scholarships = [],
      cardInfo,
    },
  } = useHomePageStore((state) => state);
  const { userData } = useAuthStore((state) => state.authSession);
  const { userInfo } = useUserInfoStore((state) => state);

  const { setSearchFilters } = useMajorSearchStore((state) => state);

  const [isOpenSchoolPartnerDialog, setIsOpenSchoolPartnerDialog] = useState(userInfo?.notifyTenantAvailable);

  const displayName = userData?.displayName || userData?.fullName.split(' ')[0] || '';

  const handleCloseSuccessDialog = () => {
    setIsOpenSchoolPartnerDialog(false);
  };

  const collegeUrl = cardInfo.collegeUrl?.match(/^[a-zA-Z]+:\/\//)
    ? cardInfo.collegeUrl
    : `https://${cardInfo.collegeUrl}`;

  const handleSetFilter = (filter: string, value: boolean) => () => {
    setSearchFilters({ [filter]: value });
  };

  return (
    <>
      <CoreBox sx={sxStyles.heroBg}>
        <StyledHeroSection>
          <StyledHeroText>
            <Breadcrumbs currentPage={cardInfo.name} />

            <CoreTypography data-testid="landingPageTitle" sx={sxStyles.heroTitle} variant="h1">
              {displayName ? (
                <CoreBox component="span">
                  <CoreBox component="span" sx={sxStyles.capitalize}>
                    {displayName}
                  </CoreBox>
                  , welcome&nbsp;
                </CoreBox>
              ) : (
                'Welcome '
              )}
              to {cardInfo.name}!
            </CoreTypography>

            <CoreTypography sx={sxStyles.heroSubtitle} component="p">
              Here you can explore majors offered by an Area of Study, favorite majors, and select a major.
            </CoreTypography>

            <CoreBox sx={sxStyles.heroActionsWrap}>
              <StyledSchoolDetailsBtn to={getSchoolDetailsPath(id)}>View School Details</StyledSchoolDetailsBtn>

              <StyledWebsiteLink data-testid="schoolWebsite" target="_blank" href={collegeUrl}>
                <UrlIcon />
                Visit Website
              </StyledWebsiteLink>
            </CoreBox>
          </StyledHeroText>

          <StyledHeroImg>
            <img src={cardInfo.pictureUrl || defaultSchoolImg} alt="school-view" />
          </StyledHeroImg>
        </StyledHeroSection>
      </CoreBox>

      {favoriteMajors?.length > 0 && (
        <CardSlider
          cardType={CardType.MAJOR_WITH_IMAGE}
          cardList={favoriteMajors?.slice(0, SLIDER_ITEMS_LIMIT)}
          title="Your favorite majors"
          buttonTitle="Go to favorites"
          navigateLink={ROUTES.FAVORITE_MAJORS}
        />
      )}

      <RelatedScholarships scholarships={scholarships} />

      <AreasSection areasList={areasOfStudy} />

      <CardSlider
        cardType={CardType.MAJOR}
        cardList={fitMajors}
        title="Recommended majors based on your strong overall fit careers"
        buttonTitle="Explore more"
        navigateLink={ROUTES.MAJORS}
        onButtonClick={handleSetFilter(searchFilters.personalRecommendation.fieldNames.isBestFitSoc, true)}
        dataTestId="exploreMoreBasedOnFit"
      />

      <CardSlider
        cardType={CardType.MAJOR}
        cardList={favoritedCareerMajors}
        title="Recommended majors based on careers you have favorited"
        buttonTitle="Explore more"
        navigateLink={ROUTES.MAJORS}
        onButtonClick={handleSetFilter(searchFilters.personalRecommendation.fieldNames.isFavoriteSoc, true)}
        dataTestId="recommendedMajorsBasedOnFavoriteCareers"
      />

      {isOpenSchoolPartnerDialog ? (
        <Suspense fallback={null}>
          <PartnerSchoolDialog isOpenDialog={isOpenSchoolPartnerDialog} onCloseDialog={handleCloseSuccessDialog} />
        </Suspense>
      ) : null}
    </>
  );
};
